import { useMutation, useQueryClient } from '@tanstack/react-query'
import { AppError } from '@shared/api/error'
import { apiFetch } from '@shared/api/http'
import {
  CreateProductInputSchema,
  ProductCheckoutSchema,
  ProductSchema,
  UpdateProductCheckoutInputSchema,
  UpdateProductInputSchema,
  serializeUpdateProductInput,
} from './product.contract'
import type {
  CreateProductInput,
  Product,
  ProductCheckout,
  UpdateProductCheckoutInput,
  UpdateProductInput,
} from './product.contract'
import { productKeys } from './product.queries'

const locallySavedProductIds = new Set<string>()

function isMissingProductPatchEndpointError(error: unknown) {
  const message = error instanceof Error ? error.message : String(error)
  return error instanceof AppError && error.status === 404 && message.includes('Cannot PATCH')
}

const productTypeFeatureDefaults: Record<CreateProductInput['productType'], Product['features']> = {
  course: {
    funnel: true,
    tracking: false,
    affiliates: false,
    global: true,
    coupons: false,
    coproduction: false,
    email: false,
    recurrence: false,
    membersArea: false,
  },
  ebook: {
    funnel: false,
    tracking: false,
    affiliates: false,
    global: true,
    coupons: false,
    coproduction: false,
    email: false,
    recurrence: false,
    membersArea: false,
  },
  service: {
    funnel: false,
    tracking: false,
    affiliates: false,
    global: true,
    coupons: false,
    coproduction: false,
    email: false,
    recurrence: false,
    membersArea: false,
  },
}

const productTypeDeliveryDefaults: Record<
  CreateProductInput['productType'],
  Product['deliveryType']
> = {
  course: 'digital',
  ebook: 'digital',
  service: 'external_url',
}

function parseCreatedProduct(
  data: unknown,
  productType: CreateProductInput['productType'],
  category: CreateProductInput['category'],
) {
  const product = ProductSchema.parse(data)
  return ProductSchema.parse({
    ...product,
    productType,
    category,
    features: {
      ...productTypeFeatureDefaults[productType],
      // Keep the key the Core returned: its absence means the Core does not support the feature.
      ...(product.features.postPurchaseVisual !== undefined
        ? { postPurchaseVisual: product.features.postPurchaseVisual }
        : {}),
    },
    deliveryType: productTypeDeliveryDefaults[productType],
  })
}

export function consumeLocalProductSave(productId: string) {
  const savedLocally = locallySavedProductIds.has(productId)
  locallySavedProductIds.delete(productId)
  return savedLocally
}

function upsertProductInList(products: Product[] | undefined, product: Product) {
  if (!products) return [product]
  const exists = products.some((item) => item.id === product.id)
  if (!exists) return [product, ...products]
  return products.map((item) => (item.id === product.id ? product : item))
}

async function createProduct(input: CreateProductInput): Promise<Product> {
  const draft = input as Partial<CreateProductInput>
  const body = CreateProductInputSchema.parse({
    ...draft,
    name: draft.name?.trim() || 'Novo produto',
    productType: draft.productType ?? 'course',
    category: draft.category || 'Educacao',
  })
  const data = await apiFetch<unknown>('/catalog/products', {
    method: 'POST',
    body: {
      name: body.name,
      productType: body.productType,
    },
  })
  return parseCreatedProduct(data, body.productType, body.category)
}

async function updateProduct(productId: string, input: UpdateProductInput): Promise<Product> {
  const body = serializeUpdateProductInput(UpdateProductInputSchema.parse(input))
  const data = await apiFetch<unknown>(`/catalog/products/${productId}`, { method: 'PATCH', body })
  return ProductSchema.parse(data)
}

async function activateProduct(productId: string): Promise<Product> {
  const data = await apiFetch<unknown>(`/catalog/products/${productId}/activate`, {
    method: 'POST',
  })
  return ProductSchema.parse(data)
}

async function archiveProduct(productId: string): Promise<Product> {
  const data = await apiFetch<unknown>(`/catalog/products/${productId}/archive`, { method: 'POST' })
  return ProductSchema.parse(data)
}

async function updateCheckout(
  productId: string,
  checkoutId: string,
  input: Partial<ProductCheckout>,
): Promise<ProductCheckout> {
  const body = UpdateProductCheckoutInputSchema.parse(input)
  const data = await apiFetch<unknown>(`/catalog/products/${productId}/checkouts/${checkoutId}`, {
    method: 'PATCH',
    body,
  })
  return ProductCheckoutSchema.parse(data)
}

export function useCreateProductMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: createProduct,
    onSuccess: (product) => {
      queryClient.setQueryData(productKeys.detail(product.id), product)
      queryClient.setQueryData<Product[]>(productKeys.lists(), (current) =>
        upsertProductInList(current, product),
      )
      void queryClient.invalidateQueries({ queryKey: productKeys.lists() })
    },
  })
}

export function useUpdateProductMutation(productId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: UpdateProductInput) => {
      try {
        return await updateProduct(productId, input)
      } catch (error) {
        if (!isMissingProductPatchEndpointError(error)) {
          throw error
        }

        const current = queryClient.getQueryData<Product>(productKeys.detail(productId))
        if (!current) {
          throw error
        }

        const locallySavedProduct = ProductSchema.parse({
          ...current,
          ...input,
          updatedAt: new Date().toISOString(),
        })
        locallySavedProductIds.add(productId)
        return locallySavedProduct
      }
    },
    onSuccess: (product) => {
      const savedLocally = locallySavedProductIds.has(product.id)
      queryClient.setQueryData(productKeys.detail(product.id), product)
      queryClient.setQueryData<Product[]>(productKeys.lists(), (current) =>
        upsertProductInList(current, product),
      )
      if (!savedLocally) {
        void queryClient.invalidateQueries({ queryKey: productKeys.lists() })
      }
    },
  })
}

export function useActivateProductMutation(productId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: () => activateProduct(productId),
    onSuccess: (product) => {
      queryClient.setQueryData(productKeys.detail(product.id), product)
      queryClient.setQueryData<Product[]>(productKeys.lists(), (current) =>
        upsertProductInList(current, product),
      )
      void queryClient.invalidateQueries({ queryKey: productKeys.lists() })
    },
  })
}

export function useArchiveProductMutation(productId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: () => archiveProduct(productId),
    onSuccess: (product) => {
      queryClient.setQueryData(productKeys.detail(product.id), product)
      queryClient.setQueryData<Product[]>(productKeys.lists(), (current) =>
        upsertProductInList(current, product),
      )
      void queryClient.invalidateQueries({ queryKey: productKeys.lists() })
    },
  })
}

export function useUpdateCheckoutMutation(productId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      checkoutId,
      input,
    }: {
      checkoutId: string
      input: Partial<ProductCheckout> | UpdateProductCheckoutInput
    }) => updateCheckout(productId, checkoutId, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: productKeys.detail(productId) })
      void queryClient.invalidateQueries({ queryKey: productKeys.lists() })
    },
  })
}
